/**
 * NaamDarbar — Utility Functions
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merge tailwind classes safely */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format a date string to a human-readable format */
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'TBD';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Generate initials from a name */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter((w) => w.length > 0)
    .map((w) => w[0]!)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** Build display name: append jatha if present (e.g. "Bhai X Ji — Nirvair Khalsa Jatha") */
export function displayName(name: string, jathaName?: string | null): string {
  return jathaName ? `${name} — ${jathaName}` : name;
}

/** Truncate text to a max length */
export function truncate(text: string, maxLen: number = 100): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '…';
}

/**
 * Format a 24-hr time string ("HH:MM" or "HH:MM:SS") to 12-hr AM/PM.
 * e.g. "14:30" → "2:30 PM", "09:05" → "9:05 AM", "00:00" → "12:00 AM"
 */
export function formatTime(timeStr: string | null | undefined): string {
  if (!timeStr) return '';
  const [hStr, mStr] = timeStr.split(':');
  const h = parseInt(hStr ?? '0', 10);
  const m = parseInt(mStr ?? '0', 10);
  if (isNaN(h) || isNaN(m)) return timeStr;
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const mins = m.toString().padStart(2, '0');
  return `${hour12}:${mins} ${period}`;
}

/**
 * Extract a human-readable message from an API/axios error. Prefers the
 * backend's `{ detail }` payload; falls back to the provided default.
 */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (err instanceof Error && err.message && !/^request failed with status/i.test(err.message)) {
    return err.message;
  }
  return fallback;
}
